'use client';

import { JSX, ReactElement, useEffect, useState } from 'react';
import React from 'react';
import type { EditableAreaOrPageChild, MagnoliaDamNode } from '@/types/cms/magnolia';
import sanitize from '@/Util/sanitize';
import NavigationWrapper from '@/components/navigation/NavigationWrapper';
import { logger, mapToApiImage, THEMES } from '@/Util/globals';
import type { ApiPage } from '../../../api/model';
import { useModelApiClient } from '@/Util/api';
import IntroClientWrapper from '@/components/intro/IntroClientWrapper';
import ImageWrapper from '@/components/image/ImageWrapper';
import { useGlobalMGLProps } from '@/Util/GlobalMGLPropsContext';

type Props = {
  name?: string;
  introText?: string;
  introImage?: MagnoliaDamNode;
  showNavigation?: string;
  showButton?: boolean;
  theme?: string;
  leftSide?: ReactElement;
  rightSide?: ReactElement;
  headline?: string;
  disableBreadcrumb?: boolean;
  singleColumn?: ReactElement;
};

const IntroWrapperClient = (props: Props & Partial<EditableAreaOrPageChild>): JSX.Element => {
  const modelApiClient = useModelApiClient();
  const pageProps = useGlobalMGLProps();
  const { theme, headline, introText, introImage, showNavigation } = props;
  const { page, siteConfig, resolvedUrl = '' } = pageProps || {};
  const pathname = new URL(resolvedUrl).pathname;
  const [pages, setPages] = useState<ApiPage[]>([]);

  useEffect(() => {
    (async () => {
      if (showNavigation && showNavigation !== 'noNavigation') {
        try {
          const localPages = await modelApiClient.getPageProps(
            showNavigation,
            page?.['@id'] || '',
            siteConfig?.name || ''
          );
          if (localPages) {
            setPages(localPages);
          }
        } catch (error) {
          logger('error')('Unable to fetch pageProps for 404', error);
        }
      }
    })();
  }, [showNavigation, page, siteConfig]);

  return (
    <>
      {pages && (
        <NavigationWrapper
          className={theme || THEMES.blue}
          items={pages && pages.map(page => page as ApiPage)}
          disableButton
        />
      )}
      <IntroClientWrapper
        theme={theme || THEMES.blue}
        headline={headline}
        pathname={pathname}
        pageProps={pageProps}
        noCssImport
        singleColumn={
          introText ? (
            <div className="intro__single-column">
              {introImage ? (
                <div className="intro__single-column__image intro__single-column__image--right">
                  <ImageWrapper
                    image={mapToApiImage(introImage) || {}}
                    className="intro__container__image"
                    serverSideContext={pageProps?.albConfig?.context}
                    pageProps={pageProps}
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                </div>
              ) : (
                <></>
              )}
              <div
                className="intro__single-column__text"
                dangerouslySetInnerHTML={{ __html: sanitize(introText, true) }}
              />
            </div>
          ) : (
            <></>
          )
        }
      />
    </>
  );
};

export default IntroWrapperClient;
